import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { portalExpansionApi, CorrectionRequest } from '../lib/portal-expansion-api';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

export default function CorrectionsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedCorrection, setSelectedCorrection] = useState<CorrectionRequest | null>(null);
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['corrections', page, statusFilter],
    queryFn: () => portalExpansionApi.getAllCorrections(page, 20, statusFilter || undefined),
    retry: 1,
  });

  if (error) {
    return (
      <div className="error-state">
        <h2>Unable to load corrections queue</h2>
        <p>
          {(error as any)?.response?.status === 404
            ? 'This endpoint is not yet implemented in the backend.'
            : 'An error occurred loading the corrections queue.'}
        </p>
        <button onClick={() => window.location.reload()} className="btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading corrections queue...</p>
      </div>
    );
  }

  const corrections = data?.corrections || [];
  const total = data?.total || 0;

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="pagehead between">
        <div>
          <h1>Help & Corrections</h1>
          <p>Review member assistance requests and correction submissions</p>
        </div>
        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="">All statuses</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="more_info_requested">More Info Requested</option>
            <option value="resolved">Resolved</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      </div>

      {corrections.length === 0 ? (
        <div className="empty-state">
          <svg
            className="icon-large"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <h3>No correction requests found</h3>
          <p>
            {statusFilter
              ? `No requests with status "${statusFilter.replace(/_/g, ' ')}".`
              : 'There are no correction requests pending review.'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Application</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {corrections.map((correction: CorrectionRequest) => (
                  <tr key={correction.id}>
                    <td>
                      <span className="category-badge">{correction.category}</span>
                    </td>
                    <td>
                      <div className="desc-cell">{correction.description.slice(0, 80)}...</div>
                    </td>
                    <td>
                      {correction.applicationId ? (
                        <code className="app-ref">
                          {correction.applicationId.slice(0, 8)}
                        </code>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <StatusBadge status={correction.status} />
                    </td>
                    <td>{new Date(correction.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => setSelectedCorrection(correction)}
                        className="btn-small"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span>
                Page {page} of {Math.ceil(total / 20)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedCorrection && (
        <CorrectionDetailModal
          correction={selectedCorrection}
          onClose={() => setSelectedCorrection(null)}
          queryClient={queryClient}
          showToast={showToast}
        />
      )}

      <style>{`
        .pagehead {
          margin-bottom: 25px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
        }

        .pagehead.between {
          flex-wrap: wrap;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #e6e9de;
          border-radius: 8px;
          font-size: 13px;
          background: white;
          color: #424a47;
          cursor: pointer;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .error-state h2 {
          font-size: 18px;
          margin-bottom: 10px;
          color: #d14343;
        }

        .error-state p,
        .loading-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .icon-large {
          width: 48px;
          height: 48px;
          color: #68716d;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          color: #68716d;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #68716d;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #68716d;
          text-align: left;
          padding: 12px 12px;
          border-bottom: 2px solid #f2f3ee;
        }

        .data-table td {
          padding: 16px 12px;
          border-bottom: 1px solid #f2f3ee;
          font-size: 13px;
          color: #424a47;
        }

        .data-table tbody tr:hover {
          background: #fafbf8;
        }

        .category-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          background: #e8edf2;
          color: #4b607f;
          text-transform: capitalize;
        }

        .desc-cell {
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .app-ref {
          font-family: 'SF Mono', Consolas, monospace;
          font-size: 11px;
          background: #f9fafb;
          padding: 3px 6px;
          border-radius: 4px;
          color: #1f2937;
        }

        .btn,
        .btn-small,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:hover,
        .btn-small:hover,
        .btn-secondary:hover {
          background: #f6f6f3;
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
        }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 20px;
        }

        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .table-container {
            padding: 12px;
          }

          .data-table th,
          .data-table td {
            padding: 10px 8px;
            font-size: 12px;
          }

          .pagehead {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    submitted: '#3b82f6',
    under_review: '#f59e0b',
    more_info_requested: '#f59e0b',
    resolved: '#10b981',
    declined: '#ef4444',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 500,
        background: `${colors[status] || '#68716d'}15`,
        color: colors[status] || '#68716d',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function CorrectionDetailModal({
  correction,
  onClose,
  queryClient,
  showToast,
}: {
  correction: CorrectionRequest;
  onClose: () => void;
  queryClient: any;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [status, setStatus] = useState(correction.status);
  const [response, setResponse] = useState(correction.response || '');

  const respondMutation = useMutation({
    mutationFn: () =>
      portalExpansionApi.respondToCorrection(correction.id, {
        status,
        response: response.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      onClose();
      showToast('Response sent', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal">
        <div className="modal-header">
          <div>
            <h2>Correction Request</h2>
            <p className="modal-subtitle">Category: {correction.category}</p>
          </div>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="detail-section">
            <h3>Request Details</h3>
            <div className="info-row">
              <label>Submitted:</label>
              <span>{new Date(correction.createdAt).toLocaleString()}</span>
            </div>
            <div className="info-row">
              <label>Current Status:</label>
              <StatusBadge status={correction.status} />
            </div>
            {correction.applicationId && (
              <div className="info-row">
                <label>Related Application:</label>
                <code className="app-ref">{correction.applicationId}</code>
              </div>
            )}
          </div>

          <div className="detail-section">
            <h3>Member Description</h3>
            <p className="description-text">{correction.description}</p>
          </div>

          {correction.requestedChange && (
            <div className="detail-section">
              <h3>Requested Change</h3>
              <p className="description-text">{correction.requestedChange}</p>
            </div>
          )}

          {correction.memberReply && (
            <div className="detail-section reply-section">
              <h3>Member Reply</h3>
              <p className="description-text">{correction.memberReply}</p>
            </div>
          )}

          <div className="detail-section">
            <h3>Admin Response</h3>
            <div className="form-group">
              <label>Decision</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={respondMutation.isPending}
              >
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="more_info_requested">Request More Information</option>
                <option value="resolved">Resolved</option>
                <option value="declined">Declined</option>
              </select>
            </div>

            <div className="form-group">
              <label>Response Message (visible to member)</label>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Explain the decision or provide guidance..."
                rows={5}
                disabled={respondMutation.isPending}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={respondMutation.isPending}
          >
            Cancel
          </button>
          <button
            onClick={() => respondMutation.mutate()}
            className="btn-primary"
            disabled={respondMutation.isPending || !response.trim()}
          >
            {respondMutation.isPending ? 'Sending...' : 'Send Response'}
          </button>
        </div>

        <style>{`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 100;
          }

          .modal {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            border-radius: 12px;
            width: 90%;
            max-width: 700px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            z-index: 101;
          }

          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px 24px;
            border-bottom: 1px solid #f2f3ee;
          }

          .modal-header h2 {
            font-size: 18px;
            font-weight: 600;
            color: #313739;
          }

          .modal-subtitle {
            font-size: 12px;
            color: #68716d;
            margin-top: 4px;
            text-transform: capitalize;
          }

          .modal-close {
            font-size: 28px;
            line-height: 1;
            color: #68716d;
            background: none;
            border: 0;
            padding: 0;
            width: 32px;
            height: 32px;
            cursor: pointer;
          }

          .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
          }

          .detail-section {
            margin-bottom: 24px;
            padding-bottom: 24px;
            border-bottom: 1px solid #f2f3ee;
          }

          .detail-section:last-child {
            border-bottom: 0;
          }

          .detail-section h3 {
            font-size: 14px;
            font-weight: 600;
            color: #313739;
            margin-bottom: 12px;
          }

          .info-row {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
            font-size: 13px;
          }

          .info-row label {
            color: #68716d;
            font-weight: 500;
          }

          .info-row span {
            color: #313739;
          }

          .app-ref {
            font-family: 'SF Mono', Consolas, monospace;
            font-size: 12px;
            background: #f9fafb;
            padding: 4px 8px;
            border-radius: 4px;
            color: #1f2937;
          }

          .description-text {
            font-size: 13px;
            color: #313739;
            line-height: 1.6;
            background: #f9fafb;
            padding: 12px;
            border-radius: 8px;
          }

          .reply-section {
            background: #fef3c7;
            border: 1px solid #fbbf24;
            border-radius: 8px;
            padding: 16px;
          }

          .reply-section .description-text {
            background: white;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 13px;
            color: #1f2937;
          }

          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #4b607f;
          }

          .form-group select:disabled,
          .form-group textarea:disabled {
            background: #f3f4f6;
            cursor: not-allowed;
          }

          .modal-footer {
            display: flex;
            gap: 12px;
            padding: 20px 24px;
            border-top: 1px solid #f2f3ee;
            justify-content: flex-end;
          }

          .btn-primary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 0;
            background: #4b607f;
            color: white;
            cursor: pointer;
          }

          .btn-primary:hover:not(:disabled) {
            background: #3d4f68;
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 1px solid #e6e9de;
            background: white;
            color: #424a47;
            cursor: pointer;
          }

          .btn-secondary:hover {
            background: #f6f6f3;
          }

          @media (max-width: 640px) {
            .modal {
              width: 100%;
              height: 100%;
              max-width: none;
              max-height: none;
              border-radius: 0;
            }

            .modal-header,
            .modal-body,
            .modal-footer {
              padding: 16px;
            }
          }
        `}</style>
      </div>
    </>
  );
}
